<div class="analytics-page">
  <app-page-header
    title="User Analytics"
    subtitle="Advertiser signups, verification, and order activity"
    [showBackButton]="true"
    backRoute="/userhome">
    <div class="header-filters" [class.header-filters--custom]="activeFilter === 'custom'">
      <mat-form-field appearance="outline" class="support-select" subscriptSizing="dynamic">
        <mat-label>Date range</mat-label>
        <mat-select [(ngModel)]="activeFilter" (selectionChange)="onDateFilterChange()" panelClass="owner-station-filter-panel">
          @for (f of filterOptions; track f.value) {
            <mat-option [value]="f.value">{{ f.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field
        *ngIf="activeFilter === 'custom'"
        appearance="outline"
        class="support-select date-range-field"
        subscriptSizing="dynamic"
        (click)="openRangePicker()">
        <mat-label>Custom dates</mat-label>
        <mat-date-range-input [rangePicker]="rangePicker">
          <input
            matStartDate
            placeholder="Start date"
            [(ngModel)]="rangeStartDate"
            (dateChange)="onCustomRangeChange()"
            readonly
            (click)="openRangePicker(); $event.stopPropagation()" />
          <input
            matEndDate
            placeholder="End date"
            [(ngModel)]="rangeEndDate"
            (dateChange)="onCustomRangeChange()"
            readonly
            (click)="openRangePicker(); $event.stopPropagation()" />
        </mat-date-range-input>
        <button
          *ngIf="rangeStartDate || rangeEndDate"
          mat-icon-button
          matSuffix
          type="button"
          class="clear-date-btn"
          aria-label="Clear custom date range"
          (click)="clearCustomRange($event)">
          <mat-icon>close</mat-icon>
        </button>
        <mat-datepicker-toggle
          *ngIf="!rangeStartDate && !rangeEndDate"
          matIconSuffix
          [for]="rangePicker"
          (click)="$event.stopPropagation()"></mat-datepicker-toggle>
        <mat-date-range-picker #rangePicker></mat-date-range-picker>
      </mat-form-field>
    </div>
  </app-page-header>

  <div class="dashboard-loading" *ngIf="loading && !summary">
    <app-data-loader [show]="true" message="Loading user analytics..." size="medium" />
  </div>

  <ng-container *ngIf="summary">
    <div class="kpi-grid">
      <div class="analytics-stat analytics-stat--total">
        <div class="analytics-stat__icon"><mat-icon>group_add</mat-icon></div>
        <div class="analytics-stat__body">
          <span class="analytics-stat__value">{{ summary.total_signups | number }}</span>
          <span class="analytics-stat__label">Total signups</span>
          <span class="analytics-stat__meta">in selected period</span>
        </div>
      </div>
      <div class="analytics-stat analytics-stat--verified">
        <div class="analytics-stat__icon"><mat-icon>verified</mat-icon></div>
        <div class="analytics-stat__body">
          <span class="analytics-stat__value">{{ summary.verified_count | number }}</span>
          <span class="analytics-stat__label">Verified</span>
          <span class="analytics-stat__meta">completed verification</span>
        </div>
      </div>
      <div class="analytics-stat analytics-stat--pending">
        <div class="analytics-stat__icon"><mat-icon>hourglass_empty</mat-icon></div>
        <div class="analytics-stat__body">
          <span class="analytics-stat__value">{{ summary.pending_verification_count | number }}</span>
          <span class="analytics-stat__label">Pending verification</span>
          <span class="analytics-stat__meta">not yet verified</span>
        </div>
      </div>
      <div class="analytics-stat analytics-stat--rate">
        <div class="analytics-stat__icon"><mat-icon>percent</mat-icon></div>
        <div class="analytics-stat__body">
          <span class="analytics-stat__value">{{ summary.verification_rate | number:'1.0-1' }}%</span>
          <span class="analytics-stat__label">Verification rate</span>
          <span class="analytics-stat__meta">of signups in this range</span>
        </div>
      </div>
      <div class="analytics-stat analytics-stat--inactive">
        <div class="analytics-stat__icon"><mat-icon>person_off</mat-icon></div>
        <div class="analytics-stat__body">
          <span class="analytics-stat__value">{{ summary.inactive_count | number }}</span>
          <span class="analytics-stat__label">Inactive users</span>
          <span class="analytics-stat__meta">currently inactive in this range</span>
        </div>
      </div>
    </div>

    <div class="analytics-card chart-card chart-card--wide">
      <div class="chart-card__head">
        <div>
          <h3>Signups over time</h3>
          <p class="chart-card__hint">{{ signupChartHint }}</p>
        </div>
        <span class="chart-card__total" *ngIf="summary">{{ summary.total_signups | number }} total</span>
      </div>
      <div class="time-chart" *ngIf="signupChartBars.length; else noTrend">
        <div class="time-chart__axis" aria-hidden="true">
          <span>{{ signupChartMax }}</span>
          <span>{{ signupChartMid }}</span>
          <span>0</span>
        </div>
        <div class="time-chart__plot">
          <span class="time-chart__gridline"></span>
          <span class="time-chart__gridline"></span>
          <span class="time-chart__gridline"></span>
          <div class="time-chart__cols">
            <div
              *ngFor="let bar of signupChartBars"
              class="time-col"
              [attr.title]="bar.hint">
              <div class="time-col__plot">
                <span class="time-col__value" *ngIf="bar.value">{{ bar.value }}</span>
                <div
                  class="time-col__bar"
                  [class.time-col__bar--empty]="!bar.value"
                  [style.height.%]="bar.heightPct"></div>
              </div>
              <span class="time-col__label">{{ bar.label }}</span>
            </div>
          </div>
        </div>
      </div>
      <ng-template #noTrend>
        <p class="chart-empty">No signup data for this period.</p>
      </ng-template>
    </div>

    <div class="panel-row charts-row">
      <div class="analytics-card chart-card">
        <div class="chart-card__head">
          <h3>Ordered vs not ordered</h3>
        </div>
        <div class="css-chart" *ngIf="activityBars.length; else noActivity">
          <div class="css-chart-bars css-chart-bars--few">
            <div
              *ngFor="let bar of activityBars"
              class="css-bar-wrap"
              [attr.title]="bar.label + ': ' + bar.value">
              <span class="css-bar-value">{{ bar.value }}</span>
              <div class="css-bar" [class.css-bar-line]="bar.accent" [style.height.%]="bar.heightPct"></div>
              <span class="css-bar-label">{{ bar.label }}</span>
            </div>
          </div>
        </div>
        <ng-template #noActivity>
          <p class="chart-empty">No order activity data for this period.</p>
        </ng-template>
      </div>

      <div class="analytics-card chart-card" *ngIf="summary.individual_count || summary.business_count">
        <div class="chart-card__head">
          <h3>Account type</h3>
        </div>
        <div class="css-chart">
          <div class="css-chart-bars css-chart-bars--few">
            <div
              *ngFor="let bar of accountTypeBars"
              class="css-bar-wrap"
              [attr.title]="bar.label + ': ' + bar.value">
              <span class="css-bar-value">{{ bar.value }}</span>
              <div class="css-bar" [class.css-bar-line]="bar.accent" [style.height.%]="bar.heightPct"></div>
              <span class="css-bar-label">{{ bar.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-container>

  <section class="app-activity analytics-card" aria-labelledby="app-activity-heading">
    <div class="users-section__head">
      <div>
        <h3 id="app-activity-heading">App Activity</h3>
        <p class="ga-caption">via Google Analytics</p>
      </div>
    </div>

    <div *ngIf="loadingAppActivity" class="loading-state">
      <app-data-loader [show]="true" message="Loading app activity..." size="small" />
    </div>

    <p class="chart-empty" *ngIf="!loadingAppActivity && !hasAppActivity">
      No activity data available yet
    </p>

    <ng-container *ngIf="!loadingAppActivity && hasAppActivity && appActivity">
      <div class="panel-row charts-row">
        <div class="activity-block">
          <h4>Active users by country</h4>
          <div class="activity-table-wrap" *ngIf="appActivity.active_users_by_country.length; else noCountries">
            <table class="activity-table">
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Active users</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of appActivity.active_users_by_country">
                  <td>{{ row.country || "—" }}</td>
                  <td>{{ row.active_users | number }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <ng-template #noCountries>
            <p class="chart-empty">No activity data available yet</p>
          </ng-template>
        </div>

        <div class="activity-block">
          <h4>Active users by city</h4>
          <div class="activity-table-wrap" *ngIf="appActivity.active_users_by_city.length; else noCities">
            <table class="activity-table">
              <thead>
                <tr>
                  <th>City</th>
                  <th>Active users</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of appActivity.active_users_by_city">
                  <td>{{ row.city || "—" }}</td>
                  <td>{{ row.active_users | number }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <ng-template #noCities>
            <p class="chart-empty">No activity data available yet</p>
          </ng-template>
        </div>
      </div>

      <div class="panel-row charts-row">
        <div class="activity-block">
          <h4>Top events</h4>
          <div class="event-list" *ngIf="appActivity.top_events.length; else noEvents">
            <div class="event-row" *ngFor="let event of appActivity.top_events">
              <span class="event-name">{{ event.event_name || "—" }}</span>
              <span class="event-track" aria-hidden="true">
                <span class="event-bar" [style.width.%]="eventBarWidth(event.event_count)"></span>
              </span>
              <span class="event-count">{{ event.event_count | number }}</span>
            </div>
          </div>
          <ng-template #noEvents>
            <p class="chart-empty">No activity data available yet</p>
          </ng-template>
        </div>

        <div class="activity-block">
          <h4>Active users over time</h4>
          <div class="activity-line" *ngIf="activityTrendSeries.length; else noActivityTrend">
            <svg viewBox="0 0 100 40" preserveAspectRatio="none" role="img" aria-label="Active users over time">
              <polyline
                *ngIf="activityTrendSeries.length > 1"
                [attr.points]="activityLinePoints"
                fill="none"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linejoin="round"
                stroke-linecap="round"
                vector-effect="non-scaling-stroke" />
              <circle
                *ngIf="activityTrendSeries.length === 1"
                cx="50"
                cy="20"
                r="1.6"
                fill="currentColor" />
            </svg>
            <div class="activity-line__labels">
              <span *ngFor="let label of activityLineLabels">{{ label }}</span>
            </div>
          </div>
          <ng-template #noActivityTrend>
            <p class="chart-empty">No activity data available yet</p>
          </ng-template>
        </div>
      </div>
    </ng-container>
  </section>

  <div class="users-section analytics-card">
    <div class="users-section__head">
      <div>
        <h3>Advertisers</h3>
        <p *ngIf="totalUsers > 0 && !loadingUsers">
          Showing {{ paginationStart }}–{{ paginationEnd }} of {{ totalUsers }}
        </p>
      </div>
      <span class="total-count" *ngIf="!loadingUsers">{{ totalUsers }} total</span>
    </div>
    <div class="controls-bar">
      <label class="search-wrap">
        <mat-icon class="search-wrap__icon">search</mat-icon>
        <input
          type="text"
          class="search-input"
          placeholder="Search by name or email..."
          (input)="onSearch($any($event.target).value)" />
      </label>

      <mat-form-field appearance="outline" class="role-filter-field" subscriptSizing="dynamic">
        <mat-label>Account type</mat-label>
        <mat-select [(ngModel)]="accountTypeFilter" (selectionChange)="onFilterChange()" panelClass="owner-station-filter-panel">
          <mat-option *ngFor="let opt of accountTypeOptions" [value]="opt.value">{{ opt.label }}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="role-filter-field" subscriptSizing="dynamic">
        <mat-label>Activity</mat-label>
        <mat-select [(ngModel)]="activityFilter" (selectionChange)="onFilterChange()" panelClass="owner-station-filter-panel">
          <mat-option *ngFor="let opt of activityOptions" [value]="opt.value">{{ opt.label }}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="role-filter-field" subscriptSizing="dynamic">
        <mat-label>Verification</mat-label>
        <mat-select [(ngModel)]="verificationFilter" (selectionChange)="onFilterChange()" panelClass="owner-station-filter-panel">
          <mat-option *ngFor="let opt of verificationOptions" [value]="opt.value">{{ opt.label }}</mat-option>
        </mat-select>
      </mat-form-field>

    </div>

    <div *ngIf="loadingUsers" class="loading-state">
      <app-data-loader [show]="true" message="Loading advertisers..." size="small" />
    </div>

    <div class="table-wrap" *ngIf="!loadingUsers">
      <table class="users-table" *ngIf="users.length">
        <thead>
          <tr>
            <th>Name</th>
            <th>Business name / Purpose</th>
            <th>Account type</th>
            <th>Signup date</th>
            <th>Verification</th>
            <th>Order status</th>
            <th>Last activity</th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let user of users; trackBy: trackByUserId"
            class="users-table__row"
            (click)="openUserDetail(user)">
            <td>
              <div class="user-name-cell">
                <span class="staff-avatar">{{ initials(user) }}</span>
                <span>{{ displayName(user) }}</span>
              </div>
            </td>
            <td class="business-purpose-cell" [attr.title]="businessOrPurpose(user)">{{ businessOrPurpose(user) }}</td>
            <td>{{ accountTypeLabel(user.account_type) }}</td>
            <td>{{ formatSignupDate(user.signup_date) }}</td>
            <td>
              <span class="status-badge" [class.badge-verified]="user.verification_status === 'verified'" [class.badge-pending]="user.verification_status !== 'verified'">
                {{ user.verification_status === "verified" ? "Verified" : "Pending" }}
              </span>
            </td>
            <td>
              <span class="status-badge" [class.badge-ordered]="user.order_status === 'ordered'" [class.badge-not-ordered]="user.order_status !== 'ordered'">
                {{ user.order_status === "ordered" ? "Ordered" : "Not ordered" }}
              </span>
            </td>
            <td>
              <div class="last-activity-cell">
                <span *ngIf="user.is_inactive" class="status-badge badge-inactive">Inactive</span>
                {{ formatLastActivity(user.last_login) }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="empty-state" *ngIf="users.length === 0">
        <mat-icon>person_search</mat-icon>
        <p>No advertisers found matching your filters.</p>
      </div>
    </div>

    <div class="pagination" *ngIf="totalPages > 1 && !loadingUsers" role="navigation">
      <button
        type="button"
        class="pagination-btn"
        [class.disabled]="currentPage === 1"
        [disabled]="currentPage === 1"
        (click)="goToPage(currentPage - 1)">
        Previous
      </button>
      <div class="pagination-numbers">
        <button
          type="button"
          *ngFor="let page of getPaginationPages()"
          class="pagination-number"
          [class.active]="page === currentPage"
          (click)="goToPage(page)">
          {{ page }}
        </button>
      </div>
      <button
        type="button"
        class="pagination-btn"
        [class.disabled]="currentPage === totalPages"
        [disabled]="currentPage === totalPages"
        (click)="goToPage(currentPage + 1)">
        Next
      </button>
    </div>
  </div>
</div>
